// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { SearchParams } from '@api/constants';
import { useReportDownload } from '@portal/containers/reports/Reports';
import { omitSearchParam, preserveCurrentSearch, withSearchParam } from '@utils/navigationSearch';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useIsAvailable } from './SettingsRegistry';
import { type VisibleGroup, visibleNav } from './SettingsTabs';

type CardLink = { id: string; action: ReactNode };

const content = (label: string) => (
  <>
    {label}
    <ChevronRight size={16} aria-hidden />
  </>
);

export default function Overview() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const available = useIsAvailable();
  const { download, isPending, contextHolder, statusRegion } = useReportDownload();

  const groups = visibleNav(available);

  const linksOf = (group: VisibleGroup): CardLink[] => {
    if (group.kind === 'group') {
      return group.entries.map(entry => ({
        id: entry.page.key,
        action: (
          <Link
            to={preserveCurrentSearch(
              entry.page.routes.global,
              omitSearchParam(searchParams, SearchParams.REPORT),
            )}
          >
            {content(t(entry.overviewLabelKey))}
          </Link>
        ),
      }));
    }
    return group.links.map(link => ({
      id: link.report.key,
      action:
        link.kind === 'preselect' ? (
          <Link
            to={withSearchParam(
              group.page.routes.global,
              searchParams,
              SearchParams.REPORT,
              link.report.key,
            )}
          >
            {content(t(link.labelKey))}
          </Link>
        ) : (
          <button type="button" disabled={isPending} onClick={() => download(link.report)}>
            {content(t(link.labelKey))}
          </button>
        ),
    }));
  };

  const cards = groups
    .map(group => ({ group, links: linksOf(group) }))
    .filter(({ links }) => links.length > 0);

  return (
    <div className="overview">
      {contextHolder}
      {statusRegion}
      {cards.map(({ group: { key, icon: Icon, labelKey }, links }) => (
        <section key={key} className="overview-card" aria-labelledby={`overview-${key}`}>
          <Icon className="overview-card-icon" size={32} aria-hidden />
          <h2 id={`overview-${key}`}>{t(labelKey)}</h2>
          <ul>
            {links.map(({ id, action }) => (
              <li key={id} className="overview-link">
                {action}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
